<template>
  <Highcharts
    ref="chart"
    :options="chartOptions"
  />
</template>

<script>
import Highcharts from 'highcharts';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';

import { formatTooltip } from './chart-helper-default';

More(Highcharts);

export default {
  name: 'Bubble',
  components: {
    Highcharts: Chart,
  },
  props: {
    series: {
      type: Array,
      required: true,
    },
    axisMetadata: {
      type: Object,
      required: true,
    },
    overrideChartOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    chartOptions() {
      const plotLinesLabelStyle = {
        fontSize: '10px',
        fontWeight: 'bold',
        padding: '2px 5px',
        borderRadius: 'var(--ds-border-radius-pill)',
        color: 'var(--ds-color-white-text)',
        background: 'var(--ds-color-text-2)',
      };

      const axisMetadata = this.axisMetadata;
      const series = this.series;

      const decreaseOpacity = el => (el.style.opacity = '0.1');

      const increaseOpacity = el => (el.style.opacity = '1');

      const handleQuadrantLabels = (handler) => {
        const quadrantLabelEl = window.document.querySelectorAll('#quadrantLabel');
        quadrantLabelEl.forEach(handler);
      };

      const quadrantsInfo = {
        topLeft: {
          x1: axisMetadata.xAxis.minimum,
          x2: axisMetadata.xAxis.middle,
          text: 'Alto engajamento e baixo sentimento',
        },
        topRight: {
          x1: axisMetadata.xAxis.middle,
          x2: axisMetadata.xAxis.maximum,
          text: 'Alto engajamento e alto sentimento',
        },
        bottomLeft: {
          x1: axisMetadata.xAxis.minimum,
          x2: axisMetadata.xAxis.middle,
          text: 'Baixo engajamento e baixo sentimento',
          bottom: true,
        },
        bottomRight: {
          x1: axisMetadata.xAxis.middle,
          x2: axisMetadata.xAxis.maximum,
          text: 'Baixo engajamento e alto sentimento',
          bottom: true,
        },
      };

      return {
        chart: {
          type: 'bubble',
          styleMode: true,
          borderRadius: 0,
          plotBorderWidth: 1,
          spacingBottom: 3,
          spacingLeft: 0,
          spacingRight: 1,
          height: 493,
          events: {
            render(obj) {
              const chart = obj.target;

              const drawQuadrantWithLabel = ({ x1, x2, text, bottom }) => {
                const xAxis1 = chart.xAxis[0].toPixels(x1);
                const xAxis2 = chart.xAxis[0].toPixels(x2);

                const width = xAxis2 - xAxis1;

                const topRectGap = 10;

                const heightRect = (chart.yAxis[0].height / 2);
                const topPositionRect = bottom ? heightRect + topRectGap : topRectGap;

                const rect = chart.renderer
                  .rect(xAxis1, topPositionRect, width, heightRect, 0)
                  .add();

                const groupBox = rect.element.getBBox();

                const styleElementText = `
                  width: ${groupBox.width}px; 
                  display: flex; 
                  justify-content: center;
                `;

                const styleLabelText = `
                  text-align: center; 
                  color: var(--ds-color-text-2);
                  font-size: 12px;
                  font-weight: 600;
                  white-space: normal;
                  transition: all .2s ease-in-out;
                  margin-bottom: 0;
                `;

                const textElement = `
                  <div style="${styleElementText}">
                    <p style="${styleLabelText}" id="quadrantLabel">
                      ${text}
                    </p>
                  </div>
                `;

                const bottomGap = axisMetadata.quadrantBottomPosition ?? 10;
                const topLabelGap = axisMetadata.quadrantTopPosition ?? 15;

                const bottomPosition = groupBox.y + (groupBox.height - bottomGap);
                const topPosition = groupBox.y + topLabelGap;

                const label = chart.renderer
                  .text(
                    textElement,
                    groupBox.x,
                    bottom ? bottomPosition : topPosition,
                    true
                  )
                  .css({
                    fontSize: '12px',
                    zIndex: 2,
                    pointerEvents: 'none',
                  })
                  .add(rect);

                return {
                  rect,
                  label,
                };
              };

              const createQuadrantsAndSaveRef = () => {
                return Object.keys(quadrantsInfo).reduce((acc, item) => {
                  acc[item] = drawQuadrantWithLabel(quadrantsInfo[item]);

                  return acc;
                }, {});
              };

              const drawHorizontalDividingLine = () => {
                const width = chart.plotWidth;
                const height = 1;

                const positionHorizontal = chart.plotLeft;
                const halfHeightOfChart = (chart.plotHeight / 2) + chart.plotTop;
                const positionVertical = halfHeightOfChart - (height / 2) + 1;

                return chart.renderer
                  .rect(positionHorizontal, positionVertical, width, height, 0)
                  .attr({ fill: 'var(--ds-color-silver)', zIndex: 0 })
                  .add();
              };

              const drawVerticalDividingLine = () => {
                const width = 1;
                const height = chart.plotHeight;

                const halfWidthOfChart = (chart.plotWidth / 2) + chart.plotLeft;
                const positionHorizontal = halfWidthOfChart - (width / 2);
                const positionVertical = chart.plotTop;

                return chart.renderer
                  .rect(positionHorizontal, positionVertical, width, height, 0)
                  .attr({ fill: 'var(--ds-color-silver)', zIndex: 0 })
                  .add();
              };

              chart.quadrants = createQuadrantsAndSaveRef();
              chart.horizontalDividingLine = drawHorizontalDividingLine();
              chart.verticalDividingLine = drawVerticalDividingLine();
            },
            redraw(obj) {
              const chart = obj.target;
              const hasDestroy = (el) => typeof el === 'object' && 'destroy' in el;

              if (chart.quadrants) {
                Object.keys(chart.quadrants).forEach(key => {
                  const el = chart.quadrants[key];

                  if (hasDestroy(el.rect) && hasDestroy(el.label)) {
                    el.rect.destroy();
                    el.label.destroy();
                  }
                });
              }

              if (hasDestroy(chart.horizontalDividingLine)) {
                chart.horizontalDividingLine.destroy();
              }

              if (hasDestroy(chart.verticalDividingLine)) {
                chart.verticalDividingLine.destroy();
              }
            },
          },
        },
        title: {
          text: '',
        },
        legend: {
          symbolHeight: 8,
          symbolWidth: 8,
          symbolRadius: 10,
          squareSymbol: false,
          align: 'left',
          margin: 16,
          padding: 5,
          itemStyle: {
            fontWeight: 600,
          },
        },
        credits: {
          enabled: false,
        },
        xAxis: {
          title: {
            text: 'Índice de sentimento',
            style: {
              color: 'black',
              fontWeight: 600,
            },
            useHTML: true,
          },
          min: axisMetadata.xAxis.minimum,
          max: axisMetadata.xAxis.maximum,
          tickLength: 0,
          plotLines: [{
            color: 'var(--ds-color-gray-1)',
            dashStyle: 'dash',
            width: 1,
            value: axisMetadata.xAxis.middle,
            label: {
              rotation: 0,
              x: -23,
              y: 10,
              useHTML: true,
              style: plotLinesLabelStyle,
              text: 'MÉDIA',
            },
            zIndex: 3,
          }],
          labels: {
            y: 28,
            x: 12,
            style: {
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 16,
              color: 'var(--ds-color-text-2)',
            },
          },
        },
        yAxis: {
          title: {
            text: 'Índice de engajamento',
            style: {
              color: 'black',
              fontWeight: 600,
            },
            useHTML: true,
          },
          labels: {
            x: -8,
            y: 4,
            style: {
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 16,
              color: 'var(--ds-color-text-2)',
            },
          },
          min: axisMetadata.yAxis.minimum,
          max: axisMetadata.yAxis.maximum,
          maxPadding: 0.2,
          gridLineWidth: 0,
          plotLines: [{
            color: 'var(--ds-color-gray-1)',
            dashStyle: 'dash',
            width: 1,
            value: axisMetadata.yAxis.middle,
            label: {
              align: 'left',
              x: 0,
              y: 0,
              useHTML: true,
              style: plotLinesLabelStyle,
              text: 'MÉDIA',
            },
            zIndex: 3,
          }],
        },
        tooltip: {
          shared: true,
          useHTML: true,
          shadow: false,
          borderRadius: 4,
          formatter() {
            return formatTooltip(this);
          },
        },
        plotOptions: {
          series: {
            color: '{point.color}',
            dataLabels: {
              enabled: true,
              format: '{point.name}',
              color: 'black',
              useHTML: true,
              style: {
                fontSize: 12,
                fontWeight: 600,
              },
            },
            events: {
              legendItemClick: (e) => {
                this.$emit('legend-click', { legend: e.target.name });
              },
            },
            stickyTracking: false,
            point: {
              events: {
                mouseOver() {
                  handleQuadrantLabels(decreaseOpacity);
                },
                mouseOut() {
                  handleQuadrantLabels(increaseOpacity);
                },
              },
            },
          },
        },
        series,
        ...this.overrideChartOptions,
      };
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .tooltip-matrix {
    min-width: 209px;
    padding: var(--ds-spacing-stack-xs);
    border-radius: var(--ds-border-radius-sm);
    box-shadow: var(--ds-light-dropshadow-2);
    background-color: var(--ds-color-neutral);

    &__header {
      display: flex;
      align-items: center;
      font-size: var(--ds-font-size-xs);
      margin-bottom: var(--ds-spacing-stack-nano);
    }

    &__point {
      display: block;
      width: var(--ds-spacing-stack-nano);
      height: var(--ds-spacing-stack-nano);
      border-radius: var(--ds-border-radius-sm);
      margin-right: var(--ds-spacing-inline-quark);
    }

    &__name {
      font-size: var(--ds-font-size-xs);
      color: var(--ds-color-text-2);
    }

    ul {
      padding-left: 0;
      list-style: none;
    }

    li:not(:last-child) {
      margin-bottom: var(--ds-spacing-stack-nano);
    }

    &__item {
      display: flex;
      justify-content: space-between;
      font-size: var(--ds-font-size-xs);
      color: var(--ds-color-text-1);
      gap: var(--ds-spacing-stack-nano);
    }

    &__grid {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr;
      align-items: flex-end;
      gap: 4px;
    }

    &__value {
      font-weight: bold;
    }

    &__divider {
      border-top: 1px solid;
      color: var(--ds-color-overlay);
      margin-bottom: 5px;
    }

    &__variation {
      width: 150px;
    }

    &__ds-icon {
      background: var(--ds-color-icon-3);
      mask-position: center center;
      mask-repeat: no-repeat;
      mask-size: cover;
      vertical-align: middle;
      width: var(--ds-font-size-sm);
      height: var(--ds-font-size-sm);
      margin: 2px 0;
    }

    &__variation-value {
      display: flex;
      justify-content: space-between;
      gap: 2px;
    }
  }

  .highcharts-legend-item path.highcharts-point {
    stroke-width: 0;
  }
}
</style>
